import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    createUseStyles,
    Markdown,
    Popover,
    SearchBar,
    Text,
    useTheme,
} from '@Sage/hoi-poi-ui';
import classnames from 'classnames';

import SectionList from 'components/SectionList';
import { Header } from '../../../components';

import SelectionLimitPopover from '../SelectionLimitPopover';
import styles from './styles.js';

const useStyles = createUseStyles(styles, 'CompanySelection');

const CHECKBOX_COL_ID = 'selected';

const CompanySelection = ({
    companies,
    isLoading = false,
    maxSelectable,
    onConfirmClick,
    onToggleCompany,
    selectedIds,
    t,
}) => {
    // --- State
    const classes = useStyles();
    const theme = useTheme();
    const dataGridRef = useRef(null);
    const selectedIdsRef = useRef(selectedIds);
    const atMaxRef = useRef(false);
    const revealRafRef = useRef(null);
    const [search, setSearch] = useState('');
    const [isTableReady, setIsTableReady] = useState(false);

    // --- Derived state
    const filteredCompanies = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return companies;
        return companies.filter(
            (c) => c.name?.toLowerCase().includes(q) || String(c.id).toLowerCase().includes(q),
        );
    }, [companies, search]);

    const selectedCount = selectedIds.length;
    const effectiveMax =
        companies.length > 0 ? Math.min(maxSelectable, companies.length) : maxSelectable;
    const atMax = selectedCount >= effectiveMax;
    const canConfirm = selectedCount > 0;
    useEffect(() => {
        selectedIdsRef.current = selectedIds;
        atMaxRef.current = atMax;
    }, [atMax, selectedIds]);

    const [initialCheckedRows] = useState(() => {
        const rows = selectedIds.reduce((acc, id) => {
            acc[id] = true;
            return acc;
        }, {});
        return {
            [CHECKBOX_COL_ID]: {
                rows,
                areAllChecked: false,
            },
        };
    });

    // --- Handlers
    const handleCheckboxChange = useCallback(
        ({ data, isColumn }) => {
            if (isColumn || !data?.id) return;
            const isSelected = selectedIdsRef.current.includes(data.id);
            if (!isSelected && atMaxRef.current) return;
            onToggleCompany(data.id);
        },
        [onToggleCompany],
    );

    const isCompanyCheckboxDisabled = useCallback(
        (companyId) =>
            Boolean(companyId && !selectedIdsRef.current.includes(companyId) && atMaxRef.current),
        [],
    );

    const isCheckboxDisabled = useCallback(
        (_colId, _rowId, node) => isCompanyCheckboxDisabled(node?.data?.id),
        [isCompanyCheckboxDisabled],
    );

    const defaultColDef = useMemo(
        () => ({
            cellClassRules: {
                'company-list-cell--checkbox-disabled': (params) =>
                    isCompanyCheckboxDisabled(params?.data?.id),
            },
        }),
        [isCompanyCheckboxDisabled],
    );

    const handleSearchInputChange = useCallback(({ inputValue, action }) => {
        const actionType = action?.action;

        if (actionType === 'clear') {
            setSearch('');
            return;
        }

        if (actionType === 'input-change') {
            setSearch(inputValue ?? '');
        }
    }, []);

    const handleSearchClear = useCallback((_value, action) => {
        if (action?.action === 'clear') {
            setSearch('');
        }
    }, []);

    const getRowNodeId = useCallback((data) => data.id, []);

    useEffect(() => {
        return () => {
            if (revealRafRef.current) {
                cancelAnimationFrame(revealRafRef.current);
                revealRafRef.current = null;
            }
        };
    }, []);

    const revealTableWhenContentPainted = useCallback((attempt = 0) => {
        const tableEl = document.getElementById('s200uki-company-selection');
        const firstNameCell = tableEl?.querySelector(".ag-cell[col-id='name'] .w5-text-cell");
        const hasPaintedName = Boolean(firstNameCell?.textContent?.trim());

        if (hasPaintedName || attempt >= 20) {
            setIsTableReady(true);
            revealRafRef.current = null;
            return;
        }

        revealRafRef.current = requestAnimationFrame(() =>
            revealTableWhenContentPainted(attempt + 1),
        );
    }, []);

    // DataGrid default onFirstDataRendered calls sizeColumnsToFit and shifts column borders vs headers.
    // Reveal only after text cells are painted to avoid initial checkbox-only frame.
    const handleFirstDataRendered = useCallback(() => {
        if (revealRafRef.current) {
            cancelAnimationFrame(revealRafRef.current);
        }
        revealTableWhenContentPainted();
    }, [revealTableWhenContentPainted]);

    const handleDataGridRef = useCallback((api) => {
        dataGridRef.current = api;
    }, []);

    // --- Memoized props
    const headerProps = useMemo(
        () => ({
            className: classes.header,
            title: t('label_s200uki_company_selection_title'),
            description: t('label_s200uki_company_selection_subtitle'),
            overrides: {
                title: { type: 'subtitle', medium: true, color: theme.colors.utility.textPrimary },
                description: { type: 'body', color: theme.colors.utility.textSecondary },
            },
        }),
        [classes.header, t, theme],
    );

    const searchBarProps = useMemo(
        () => ({
            className: classes.searchBar,
            placeholder: t('label_s200uki_search_company'),
            useAsSimpleSearch: true,
            isFullWidth: true,
            inputValue: search,
            hideDropdownIndicator: true,
            overrides: {
                Select: {
                    customOnChangeInput: handleSearchInputChange,
                    onChange: handleSearchClear,
                    overrides: {
                        control: {
                            style: {
                                backgroundColor: theme.colors.transparent,
                                borderRadius: 0,
                                borderColor: `${theme.colors.transparent} ${theme.colors.transparent} ${theme.colors.grey[800]} !important`,
                            },
                        },
                        controlFocused: {
                            style: {
                                backgroundColor: theme.colors.actionMinor[50],
                            },
                        },
                        placeholder: {
                            style: {
                                color: theme.colors.grey[800],
                            },
                        },
                    },
                },
            },
        }),
        [
            classes.searchBar,
            handleSearchClear,
            handleSearchInputChange,
            search,
            t,
            theme.colors.actionMinor,
            theme.colors.grey,
            theme.colors.transparent,
        ],
    );

    const selectionLimitPopoverContent =
        atMax && maxSelectable > 1 ? t('label_s200uki_company_selection_limit') : undefined;

    useEffect(() => {
        dataGridRef.current?.refreshColumnByColId?.([CHECKBOX_COL_ID]);
        dataGridRef.current
            ?.getApi?.()
            ?.refreshCells?.({ columns: ['name', 'id', CHECKBOX_COL_ID], force: false });
    }, [atMax, selectedIds, selectionLimitPopoverContent]);

    const columns = useMemo(
        () => [
            {
                colId: 'name',
                field: 'name',
                headerName: t('label_s200uki_company_name'),
                headerComponent: 'headerTextCell',
                flex: 1,
                minWidth: 160,
                resizable: false,
                suppressSizeToFit: true,
                cellRenderer: 'textCell',
            },
            {
                colId: 'id',
                field: 'id',
                headerName: t('label_s200uki_company_id'),
                headerComponent: 'headerTextCell',
                width: 120,
                minWidth: 120,
                maxWidth: 120,
                resizable: false,
                suppressSizeToFit: true,
                cellRenderer: 'textCell',
                cellClass: classes.companyIdCell,
                cellRendererParams: {
                    align: 'right',
                    alignHeader: 'right',
                },
            },
            {
                colId: CHECKBOX_COL_ID,
                headerName: '',
                headerComponent: 'headerTextCell',
                width: 72,
                minWidth: 72,
                maxWidth: 72,
                resizable: false,
                suppressSizeToFit: true,
                cellRenderer: 'textCell',
                cellRendererParams: {
                    align: 'center',
                    alignHeader: 'center',
                    checkbox: {
                        onChange: handleCheckboxChange,
                        isDisabled: isCheckboxDisabled,
                        isAlwaysVisible: true,
                        noCheckedWhenDisabled: true,
                    },
                },
            },
        ],
        [classes.companyIdCell, handleCheckboxChange, isCheckboxDisabled, t],
    );

    const emptyViewProps = useMemo(
        () => ({
            title: t('label_s200uki_search_no_results_title'),
            subtitle: t('label_s200uki_search_no_results_subtitle'),
        }),
        [t],
    );

    const sectionListProps = useMemo(
        () => ({
            id: 's200uki-company-selection',
            className: classnames(classes.companyList, !isTableReady && classes.companyListHidden),
            columns,
            rows: filteredCompanies,
            checkedRows: initialCheckedRows,
            getRowNodeId,
            domLayout: 'normal',
            headerHeight: 36,
            rowHeight: 44,
            suppressColumnVirtualisation: true,
            suppressRowClickSelection: true,
            suppressHorizontalScroll: true,
            onFirstDataRendered: handleFirstDataRendered,
            onGridSizeChanged: handleFirstDataRendered,
            defaultColDef,
            onRef: handleDataGridRef,
            emptyViewProps,
        }),
        [
            classes.companyList,
            classes.companyListHidden,
            columns,
            defaultColDef,
            emptyViewProps,
            filteredCompanies,
            getRowNodeId,
            handleDataGridRef,
            handleFirstDataRendered,
            initialCheckedRows,
            isTableReady,
        ],
    );

    const confirmButtonProps = useMemo(
        () => ({
            type: 'primary',
            size: 'big',
            isDisabled: !canConfirm,
            isLoading,
            onClick: onConfirmClick,
            children: t('label_s200uki_company_selection_cta'),
        }),
        [canConfirm, isLoading, onConfirmClick, t],
    );

    const confirmHintPopoverContent = useMemo(
        () => (
            <Text type="body" color={theme.colors.utility.textPrimary}>
                {t('label_s200uki_company_selection_hint')}
            </Text>
        ),
        [t, theme.colors.utility.textPrimary],
    );

    const confirmHintPopoverProps = useMemo(
        () => ({
            content: confirmHintPopoverContent,
            placement: 'top-end',
            trigger: ['hover'],
        }),
        [confirmHintPopoverContent],
    );

    // --- Render
    return (
        <div className={classes.root}>
            <Header {...headerProps} />
            <div className={classes.searchRow}>
                <SearchBar {...searchBarProps} />
                <Text type="body" medium className={classes.selectedCount}>
                    {t('label_s200uki_companies_selected', [
                        String(selectedCount),
                        String(maxSelectable),
                    ])}
                </Text>
            </div>
            <div className={classes.tableArea}>
                <SelectionLimitPopover
                    checkboxColId={CHECKBOX_COL_ID}
                    content={selectionLimitPopoverContent}
                    isEnabled={Boolean(selectionLimitPopoverContent)}
                />
                <SectionList {...sectionListProps} />
            </div>
            <Markdown
                content={t('label_s200uki_companies_selection_hint')}
                className={classes.selectionHint}
                overrides={{
                    p: {
                        type: 'caption',
                        medium: true,
                    },
                    a: {
                        type: 'caption',
                        medium: true,
                        variation: 'primary',
                        target: '_blank',
                    },
                }}
            />
            <div className={classes.footerActions}>
                {!canConfirm ? (
                    <Popover {...confirmHintPopoverProps}>
                        <span className={classes.confirmButtonTrigger}>
                            <Button {...confirmButtonProps} />
                        </span>
                    </Popover>
                ) : (
                    <Button {...confirmButtonProps} />
                )}
            </div>
        </div>
    );
};

CompanySelection.propTypes = {
    companies: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            status: PropTypes.string,
        }),
    ).isRequired,
    isLoading: PropTypes.bool,
    maxSelectable: PropTypes.number.isRequired,
    onConfirmClick: PropTypes.func.isRequired,
    onToggleCompany: PropTypes.func.isRequired,
    selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    t: PropTypes.func.isRequired,
};

CompanySelection.displayName = 'CompanySelection';

export default memo(CompanySelection);
